// components/Orders/AssignRiderPanel.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Hand an order item to a rider. One panel, used by the admin order screen and
// the vendor's own view of the same order.
//
// WHAT WAS WRONG WITH THE TWO IT REPLACES
//
//   Gating. The admin screen wrote:
//
//       {(o.delivery_status !== "REJECTED" && o.delivery_status === "ASSIGNED") ||
//         (o.delivery_status === "PROCESSING" && someItemProcessing && (<div/>))}
//
//   `&&` binds tighter than `||`, so on an ASSIGNED order the expression
//   short-circuits to the boolean `true` — and React renders a bare `true` as
//   nothing at all. The panel vanished exactly when someone needed to assign
//   the SECOND item of a two-item order, or move an item to a different rider.
//   The vendor screen's version, `!o.delivery_status === "ASSIGNED"`, compares
//   a boolean to a string and is therefore always false; it worked only by
//   accident of the `||` that followed.
//
//   Disabled state. Both rendered `variant="contained" color="primary"` (MUI
//   props on a react-bootstrap Button, ignored) plus a permanent grey
//   `disabledButton` class and a `data-disabled` attribute that styled a cursor
//   and nothing else. The button was always grey and always clickable: it
//   looked disabled when it worked, and worked when it looked disabled.
//
// Here the button carries the real `disabled` attribute, and whenever it is
// disabled the panel says in words what is missing.
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import { Form, Spinner } from "react-bootstrap";
import { riderLockState, riderLockMessage } from "../../utilities/orderRiderLock";
import { useGate, GATE_TITLE } from "../team/Gated";
import AppSelect from "../ui/AppSelect";
import { isRiderOnline, riderBadgeProps, riderName, sortRidersOnlineFirst } from "./riderStatus";

const AssignRiderPanel = ({
  order,
  riders,
  loadingRiders,
  ridersError,
  riderId,
  onRiderChange,
  selectedItemId,
  needsItemSelection,
  onAssign,
  assigning,
  currentRiderName,
}) => {
  // Vendor team permissions: assigning a rider is orders:manage. Owners,
  // platform staff and 3PL logins are never gated (can answers true).
  const ordersGate = useGate("orders:manage");
  const lock = riderLockState(order);
  if (lock) {
    return <div className="rod-hint">{riderLockMessage(lock)}</div>;
  }

  const missingItem = needsItemSelection && !selectedItemId;
  const disabled = !riderId || missingItem || loadingRiders || assigning || !ordersGate.allowed;

  const reason = !ordersGate.allowed
    ? GATE_TITLE
    : ridersError
    ? ridersError
    : loadingRiders
    ? "Finding riders who are online."
    : missingItem
    ? "Pick the item to hand over, then choose a rider."
    : !riderId
    ? "Choose a rider to hand this over to."
    : null;

  const online = (riders || []).filter(isRiderOnline).length;
  const riderOptions = sortRidersOnlineFirst(riders);

  return (
    <div>
      {currentRiderName && (
        <div className="rod-hint" style={{ marginBottom: 10 }}>
          Currently with <strong>{currentRiderName}</strong>. Choosing another
          rider reassigns it.
        </div>
      )}

      <Form.Group style={{ marginBottom: 10 }}>
        <Form.Label
          style={{
            fontSize: "0.66rem",
            textTransform: "uppercase",
            letterSpacing: "0.7px",
            fontWeight: 700,
            color: "#64748b",
          }}
        >
          Rider
          {!loadingRiders && riders?.length > 0 && (
            <span style={{ fontWeight: 500, letterSpacing: 0 }}>
              {"  "}
              {online} of {riders.length} online
            </span>
          )}
        </Form.Label>
        <AppSelect block
          value={riderId || ""}
          disabled={loadingRiders || !!ridersError || !ordersGate.allowed}
          title={ordersGate.title}
          onChange={(e) => onRiderChange(e.target.value)}
        >
          <option value="">
            {loadingRiders ? "Searching for riders..." : "Select a rider"}
          </option>
          {riderOptions.map((rider) => (
            <option key={rider._id} value={rider._id} {...riderBadgeProps(rider)}>
              {riderName(rider)}
            </option>
          ))}
        </AppSelect>
      </Form.Group>

      <div className="rod-actions">
        <button
          type="button"
          className="lk-action-btn lk-action-btn--lg"
          disabled={disabled}
          title={ordersGate.title}
          onClick={onAssign}
        >
          {assigning && (
            <Spinner animation="border" size="sm" role="status" aria-hidden />
          )}
          {currentRiderName ? "Reassign to this rider" : "Assign to rider"}
        </button>
        {reason && (
          <span className={`rod-hint ${ridersError ? "rod-hint-warn" : ""}`}>
            {reason}
          </span>
        )}
      </div>
    </div>
  );
};

export default AssignRiderPanel;
